import { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import trySendMessage from './trySendMessage';
import composeStore from '../store/composeStore';
import ComposeTarget from '../store/schema/ComposeTarget';
import { ComposeOperation } from '../store/schema/ComposeViewState';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import { getToCcRecipientsForReply } from '../utils/createReply';
import getDefaultBodyType from '../utils/getDefaultBodyType';
import getRespondSubject from '../utils/getRespondSubject';
import { getGuid } from 'owa-guid';
import { ClientItem } from 'owa-mail-store';
import { isDeepLink } from 'owa-popout';
import { isSxSDisplayed } from 'owa-sxs-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { action, select } from 'satcheljs/lib/legacy';

export interface ReplyToApprovalMessageState {
    primaryComposeId: string;
}

export default action('replyToApprovalMessage')(
    select<ReplyToApprovalMessageState>({
        primaryComposeId: () => composeStore.primaryComposeId,
    })(async function replyToApprovalMessage(
        referenceItem: ClientItem,
        isApprove: boolean,
        shouldSend: boolean,
        state?: ReplyToApprovalMessageState
    ) {
        const openInSxS = isSxSDisplayed();
        const openInTab = !isDeepLink() && isTabViewEnabled();
        if (!openInSxS && !openInTab) {
            await trySaveAndCloseCompose(null /*viewState*/);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (state.primaryComposeId) {
                return Promise.resolve();
            }
        }

        // If traceId is not passed create new one so we can correlate between all replies and sent
        const composeTraceId = getGuid();
        const composeOperation = isApprove ? ComposeOperation.Approve : ComposeOperation.Reject;
        const subject = getRespondSubject(referenceItem, composeOperation);
        const [toRecipients] = await getToCcRecipientsForReply(referenceItem, false /*isReplyAll*/);

        const composeInitProps: ComposeViewStateInitProps = {
            operation: composeOperation,
            bodyType: getDefaultBodyType(),
            subject: subject,
            to: toRecipients,
            referenceItemId: referenceItem.ItemId,
            preferAsyncSend: true,
            logTraceId: composeTraceId,
            isPopout: isDeepLink(),
        };
        const composeViewState = await prepareComposeViewState(composeInitProps);

        const fullComposeTarget = openInTab
            ? ComposeTarget.SecondaryTab
            : ComposeTarget.PrimaryReadingPane;

        await openComposeWithViewState(composeViewState, fullComposeTarget);

        if (shouldSend) {
            trySendMessage(composeViewState, true /* bypassClientSideValidation */);
        }
    })
);
